/**
 * Ручки Callog API, по одному вызову на эндпоинт.
 *
 * Ручек, которых у бэкенда ещё нет (массовые действия, интеграции), здесь нет
 * намеренно: заглушка, возвращающая моки под видом API, — самый дорогой вид
 * вранья.
 */

import { apiDownload, apiRequest } from './client';
import type {
  AdName,
  AdNameInput,
  AnalysisInputEntry,
  CallGoal,
  CallGoalDryRun,
  CallGoalDryRunInput,
  CallGoalInput,
  CallGoalStatus,
  CtHandlingReport,
  HandoffReport,
  UnitPromptAddendum,
  UnitPromptAddendumInput,
  AdminOfTenant,
  AdminOrganization,
  AdminTenant,
  AdminTenantInput,
  AnalysisSettings,
  AnalysisSettingsInput,
  Assignment,
  AssignmentClosed,
  AssignmentCreated,
  AssignmentInput,
  AudioLink,
  Binding,
  BindingInput,
  CallComment,
  CallDetail,
  CallField,
  CallFieldInput,
  CallFieldTemplate,
  CallFilters,
  CallPage,
  CallTag,
  ConnectionReport,
  Contact,
  ContactInput,
  ConversionFilters,
  ConversionRow,
  CtAvailableNumber,
  CtEnrollRequest,
  CtEnrollResult,
  CtNumber,
  CtNumberPatch,
  CtPool,
  CtPoolInput,
  CtSession,
  CtSessionFilters,
  CtSitesReport,
  CtSourcesFilters,
  CtSourcesReport,
  CtSite,
  CtSiteInput,
  CtStaticRule,
  CtStaticRuleInput,
  DashboardMetrics,
  DeliveryFilters,
  DepartmentSuggestionStatus,
  DestinationRoutingInput,
  Direction,
  DirectoryDestination,
  DirectoryExtension,
  DirectoryFilters,
  DirectoryGateway,
  DirectoryQueue,
  DisclosureFilters,
  DisclosureGrant,
  DisclosureGrantInput,
  Integration,
  IntegrationInput,
  IntegrationDictionaries,
  IntegrationKind,
  IntegrationLookupTest,
  IntegrationMapping,
  IntegrationMappingsInput,
  IntegrationSecretsInput,
  IntegrationTestResult,
  Employee,
  EmployeeFilters,
  EmployeeInput,
  EmployeeRulesData,
  GrantInput,
  HeatmapData,
  Me,
  MissedAction,
  MissedActionInput,
  MissedCallsPage,
  OutboundDelivery,
  MissedFilters,
  Notification,
  NotificationContactsInput,
  NotificationDelivery,
  NotificationRule,
  NotificationRuleInput,
  NotificationSettings,
  NotificationsPage,
  PbxServer,
  PbxServerInput,
  PbxServerWithSecret,
  PendingOrganization,
  RatingData,
  ReportFilters,
  ReportKind,
  ReviewResult,
  Rule,
  RuleInput,
  RuleTestRequest,
  RuleTestResponse,
  Scorecard,
  ScorecardDirection,
  ScorecardTemplate,
  ScoreDynamicsData,
  Source,
  SourceInput,
  SourceWithSecret,
  StorageProfile,
  StorageProfileInput,
  StorageTestResult,
  SummaryReportData,
  Tag,
  TagInput,
  TagRule,
  TagRuleConditions,
  TagRuleInput,
  TagRuleTestResult,
  TagTemplate,
  TagTemplateResult,
  TopViolationsData,
  Unit,
  UnitInput,
  UnitScorecards,
  User,
  ViolationsData,
  VocabularyInput,
  VocabularyItem,
  WebhookDraftTest,
  WebhookTestResult,
} from './types';

// ─────────────────────────────── Личность ───────────────────────────────

/**
 * Первый вызов нового пользователя заметно медленнее: бэкенд заводит строку
 * и добирает email с именем из userinfo Zitadel. Это не сбой.
 */
export const getMe = () => apiRequest<Me>('/api/me');

// ─────────────────────────── Подразделения ──────────────────────────────

/**
 * Что назначено на узле и что реально применится к звонкам.
 *
 * `effective` всегда содержит три элемента — по одному на направление.
 * Экран узла строится по нему: клиента интересует «что будет со звонком», а
 * не «какие строки лежат в базе».
 */
export const getUnitScorecards = (id: string) =>
  apiRequest<UnitScorecards>(`/api/units/${id}/scorecard`);

/**
 * Назначить чек-лист на направление звонка.
 *
 * Направление обязательно, потому что списки разные по сути: входящий
 * проверяет, как оператор принял обращение, исходящий — как он его начал.
 * Один список на оба занижал бы балл там, где человек всё сделал верно.
 *
 * `scorecardId: null` снимает назначение — тогда снова действует чек-лист
 * предка. Отвечает `204`.
 */
export const setUnitScorecard = (
  id: string,
  direction: ScorecardDirection,
  scorecardId: string | null
) =>
  apiRequest<void>(`/api/units/${id}/scorecard`, {
    method: 'PUT',
    body: { direction, scorecardId },
  });

/** Дерево целиком и уже вложенное. Отдельного «мои узлы» не нужно. */
export const listUnits = () => apiRequest<Unit[]>('/api/units');

export const createUnit = (body: UnitInput) =>
  apiRequest<Unit>('/api/units', { method: 'POST', body });

export const updateUnit = (id: string, body: UnitInput) =>
  apiRequest<Unit>(`/api/units/${id}`, { method: 'PUT', body });

/** Архивирует, а не удаляет: на узел ссылаются звонки и счета. */
export const archiveUnit = (id: string) =>
  apiRequest<void>(`/api/units/${id}`, { method: 'DELETE' });

// ───────────────────── Источники и маршруты ─────────────────────────────

/**
 * В чьём тенанте работаем.
 *
 * **Читается только у `platform_staff`.** Источники и маршруты живут в
 * тенанте клиента, а сотрудник платформы сидит в своём: без параметра
 * источник заведётся ему самому — молча, с ответом 201, и звонки клиента в
 * него не пойдут никогда. Поэтому параметр есть у каждой ручки раздела, а не
 * у тех, где о нём вспомнили.
 *
 * У остальных он не отвергается, а игнорируется: отказ по чужому tenant
 * подсказал бы, что такой клиент существует.
 */
const scope = (tenant?: string) => (tenant ? { tenant } : undefined);

export const listSources = (tenant?: string) =>
  apiRequest<Source[]>('/api/sources', { query: scope(tenant) });

/** Возвращает секрет — единственный раз за всю жизнь источника. */
export const createSource = (body: SourceInput, tenant?: string) =>
  apiRequest<SourceWithSecret>('/api/sources', { method: 'POST', body, query: scope(tenant) });

export const updateSource = (id: string, body: SourceInput, tenant?: string) =>
  apiRequest<Source>(`/api/sources/${id}`, { method: 'PUT', body, query: scope(tenant) });

/** Старый ключ умирает в момент ротации: показать новый сразу и предупредить. */
export const rotateSourceSecret = (id: string, tenant?: string) =>
  apiRequest<SourceWithSecret>(`/api/sources/${id}/rotate-secret`, {
    method: 'POST',
    query: scope(tenant),
  });

/** Проверка готовности нашей стороны приёма, а не дозвон до АТС. */
export const testSourceConnection = (id: string, tenant?: string) =>
  apiRequest<ConnectionReport>(`/api/sources/${id}/test-connection`, {
    method: 'POST',
    query: scope(tenant),
  });

export const listBindings = (sourceId: string, tenant?: string) =>
  apiRequest<Binding[]>(`/api/sources/${sourceId}/bindings`, { query: scope(tenant) });

export const createBinding = (sourceId: string, body: BindingInput, tenant?: string) =>
  apiRequest<Binding>(`/api/sources/${sourceId}/bindings`, {
    method: 'POST',
    body,
    query: scope(tenant),
  });

/** Дерево клиента: узел маршрута принадлежит ему, а не нам. */
export const listTenantUnits = (tenantId: string) =>
  apiRequest<Unit[]>(`/api/admin/tenants/${tenantId}/units`);

// ─────────────────────────────── Звонки ─────────────────────────────────

/**
 * Список срезан областью видимости на стороне БД. Фильтры её сужают, но
 * расширить не могут — дублировать эту логику на клиенте нельзя.
 */
export const listCalls = (filters: CallFilters = {}, signal?: AbortSignal) =>
  apiRequest<CallPage>('/api/calls', { query: filters as Record<string, unknown>, signal });

export const getCall = (id: string) => apiRequest<CallDetail>(`/api/calls/${id}`);

/**
 * Отдаёт подписанную ссылку на пять минут, а не файл. Перевыпрашивать надо
 * по expiresIn, до протухания: ловить 410 в середине прослушивания — плохо.
 */
export const getCallAudio = (id: string) => apiRequest<AudioLink>(`/api/calls/${id}/audio`);

// ─────────────────── Дополнительные поля разбора ────────────────────────

export const listCallFields = () => apiRequest<CallField[]>('/api/call-fields');

export const listCallFieldTemplates = () =>
  apiRequest<CallFieldTemplate[]>('/api/call-fields/templates');

/**
 * Существующие поля не трогает: в ответе `skipped` — сколько пропущено,
 * потому что ключ уже занят. Число надо показать, иначе человек решит, что
 * схема заменилась целиком.
 */
export const applyCallFieldTemplate = (template: string) =>
  apiRequest<{ added: number; skipped: number }>('/api/call-fields/from-template', {
    method: 'POST',
    body: { template },
  });

export const createCallField = (body: CallFieldInput) =>
  apiRequest<CallField>('/api/call-fields', { method: 'POST', body });

export const updateCallField = (id: string, body: CallFieldInput) =>
  apiRequest<CallField>(`/api/call-fields/${id}`, { method: 'PUT', body });

/**
 * Удаление **не стирает** уже извлечённые значения. Чтобы просто перестать
 * собирать, достаточно `active: false`.
 */
export const deleteCallField = (id: string) =>
  apiRequest<void>(`/api/call-fields/${id}`, { method: 'DELETE' });

// ──────────────────────────── Оповещения ───────────────────────────────

export const listNotifications = (unread?: boolean, limit = 50) =>
  apiRequest<NotificationsPage>('/api/notifications', { query: { unread, limit } });

/**
 * Отвечает `204` и на уже прочитанное, и на чужое: повторное нажатие не
 * ошибка, а по чужому id незачем узнавать, что оповещение существует.
 */
export const markNotificationRead = (id: string) =>
  apiRequest<void>(`/api/notifications/${id}/read`, { method: 'POST' });

export const markAllNotificationsRead = () =>
  apiRequest<void>('/api/notifications/read-all', { method: 'POST' });

export const listNotificationRules = () =>
  apiRequest<NotificationRule[]>('/api/notification-rules');

export const createNotificationRule = (body: NotificationRuleInput) =>
  apiRequest<NotificationRule>('/api/notification-rules', { method: 'POST', body });

export const updateNotificationRule = (id: string, body: NotificationRuleInput) =>
  apiRequest<NotificationRule>(`/api/notification-rules/${id}`, { method: 'PUT', body });

export const deleteNotificationRule = (id: string) =>
  apiRequest<void>(`/api/notification-rules/${id}`, { method: 'DELETE' });

/** «Почему мне не пришло» иначе неразрешимо. */
export const listDeliveries = () =>
  apiRequest<NotificationDelivery[]>('/api/notification-rules/deliveries');

/**
 * Проверка черновика — того, что сейчас в форме и ещё не сохранено.
 *
 * Телефон, почту и шаблон передаём из полей: без них проверка отвечает лишь
 * «адрес отвечает», а администратору нужно увидеть готовое сообщение с тем
 * телефоном, который он вписал.
 */
export const testWebhookDraft = (body: WebhookDraftTest) =>
  apiRequest<WebhookTestResult>('/api/notification-rules/test', { method: 'POST', body });

/**
 * Проверка сохранённого правила — его настоящими адресом, секретом,
 * получателем и шаблоном.
 *
 * Отличие от проверки черновика не косметическое: «адрес отвечает» при
 * незаполненном телефоне даёт зелёную галочку правилу, которое в бою
 * доставит сообщение в никуда.
 */
export const testNotificationRule = (id: string) =>
  apiRequest<WebhookTestResult>(`/api/notification-rules/${id}/test`, { method: 'POST' });

// ─────────────────────── Рабочий список пропущенных ────────────────────

/**
 * Пропущенные, которые ещё можно спасти.
 *
 * Умолчание `state=open`: экран открывают, чтобы обзвонить, а не изучать
 * архив. Сводка отвечает «сколько потеряно», список — «какие именно и что с
 * ними делать сейчас».
 */
export const listMissedCalls = (filters: MissedFilters, signal?: AbortSignal) =>
  apiRequest<MissedCallsPage>('/api/missed-calls', { query: { ...filters }, signal });

export const listMissedActions = (callId: string) =>
  apiRequest<{ items: MissedAction[] }>(`/api/missed-calls/${callId}/actions`);

/**
 * Отметка об отработке — то, без чего «потеряно» завышено: половина
 * пропущенных закрывается не звонком.
 *
 * Отметка перекрывает только «никто не перезвонил». Наблюдённый факт —
 * состоявшийся разговор с тем же человеком — она не отменяет, и `state`
 * останется `recovered`.
 */
export const createMissedAction = (callId: string, body: MissedActionInput) =>
  apiRequest<MissedAction>(`/api/missed-calls/${callId}/actions`, { method: 'POST', body });

/**
 * Снять отметку. Обязательна: ошибочное закрытие иначе остаётся навсегда, и
 * «потеряно» портится в другую сторону — занижается, а это тот же конец для
 * доверия к цифре. Снятие последней возвращает звонок в потерянные.
 */
export const deleteMissedAction = (callId: string, actionId: number) =>
  apiRequest<void>(`/api/missed-calls/${callId}/actions/${actionId}`, { method: 'DELETE' });

// ────────────────────────────── Контакты ───────────────────────────────

/** Приходит вместе с историей — до 100 звонков в области видимости. */
export const getContact = (id: string) => apiRequest<Contact>(`/api/contacts/${id}`);

/**
 * Номер передаётся как есть, в любом написании: нормализация на сервере, и
 * `87011234567` с `+7 701 123-45-67` найдут одного человека.
 *
 * `404` у добавочного — не ошибка: «206» одного салона и «206» другого это
 * разные телефоны.
 */
export const findContactByPhone = (phone: string) =>
  apiRequest<Contact>('/api/contacts/by-phone', { query: { phone } });

export const updateContact = (id: string, body: ContactInput) =>
  apiRequest<Contact>(`/api/contacts/${id}`, { method: 'PUT', body });

// ──────────────────────────────── Теги ─────────────────────────────────

/** Читает любой авторизованный: нужен для чипов и автодополнения. */
export const listTags = (search?: string) => apiRequest<Tag[]>('/api/tags', { query: { search } });

export const createTag = (body: TagInput) => apiRequest<Tag>('/api/tags', { method: 'POST', body });

export const updateTag = (id: string, body: TagInput) =>
  apiRequest<Tag>(`/api/tags/${id}`, { method: 'PUT', body });

/** Удаляет насовсем и уносит всю разметку: архива нет намеренно. */
export const deleteTag = (id: string) => apiRequest<void>(`/api/tags/${id}`, { method: 'DELETE' });

/** Возвращает весь список тегов звонка — состояние можно заменить целиком. */
export const addCallTag = (callId: string, tagId: string) =>
  apiRequest<CallTag[]>(`/api/calls/${callId}/tags`, { method: 'POST', body: { tagId } });

/**
 * Ручной тег удаляется совсем, автоматический подавляется: строка остаётся,
 * из выдачи уходит, и повторный прогон правил его не воскрешает. Человек
 * воскрешает — повторным добавлением.
 */
export const removeCallTag = (callId: string, tagId: string) =>
  apiRequest<CallTag[]>(`/api/calls/${callId}/tags/${tagId}`, { method: 'DELETE' });

export const listTagRules = () => apiRequest<TagRule[]>('/api/tag-rules');

export const createTagRule = (body: TagRuleInput) =>
  apiRequest<TagRule>('/api/tag-rules', { method: 'POST', body });

export const updateTagRule = (id: string, body: TagRuleInput) =>
  apiRequest<TagRule>(`/api/tag-rules/${id}`, { method: 'PUT', body });

/** Удаление правила **не снимает** уже проставленные теги: они часть разметки. */
export const deleteTagRule = (id: string) =>
  apiRequest<void>(`/api/tag-rules/${id}`, { method: 'DELETE' });

/**
 * Проверка на настоящем звонке. Придуманный транскрипт не принимается: тег
 * вешают за то, что реально прозвучало, а условия по номеру и длительности
 * на выдуманном звонке бессмысленны.
 */
export const testTagRule = (callId: string, conditions: TagRuleConditions) =>
  apiRequest<TagRuleTestResult>('/api/tag-rules/test', {
    method: 'POST',
    body: { callId, conditions },
  });

// ─────────────────── Комментарии и разбор оценки ────────────────────────

export const listComments = (callId: string) =>
  apiRequest<CallComment[]>(`/api/calls/${callId}/comments`);

/**
 * Пишут `supervisor`, `analyst`, `unit_admin`, `tenant_admin` — на узле
 * звонка или выше. `agent` не пишет даже к собственному разговору:
 * комментарий к своей оценке — не разбор, а возражение.
 */
export const createComment = (callId: string, text: string) =>
  apiRequest<CallComment>(`/api/calls/${callId}/comments`, { method: 'POST', body: { text } });

/** Удаляет автор либо `tenant_admin`. Удаление мягкое: строка остаётся. */
export const deleteComment = (callId: string, commentId: string) =>
  apiRequest<void>(`/api/calls/${callId}/comments/${commentId}`, { method: 'DELETE' });

/**
 * Поправить результат правила.
 *
 * Правится результат правила, а не итоговый балл: балл — производная, и
 * править его напрямую значит получить оценку, которая не сходится с
 * правилами под ней.
 *
 * Ответ — **вся карточка целиком, уже пересчитанная**. Считать новый балл,
 * полосу и признак критического нарушения на клиенте не нужно и не следует:
 * две реализации одной формулы разойдутся молча и ровно там, где это дороже
 * всего — в показателе конкретного оператора.
 */
export const reviewScoreResult = (
  callId: string,
  resultId: string,
  body: { result: ReviewResult; comment?: string }
) =>
  apiRequest<CallDetail>(`/api/calls/${callId}/score-results/${resultId}`, {
    method: 'PUT',
    body,
  });

/**
 * Решение по разговору другого отдела.
 *
 * **Фиксирует, а не переносит.** Звонок остаётся за отделом сотрудника,
 * основная оценка не меняется: `accepted` значит «да, это работа того отдела,
 * и сделал её он». По этому признаку и строится отчёт — фильтр
 * `departmentSuggestion=accepted`.
 *
 * `pending` возвращает предложение в нерассмотренные: передумать можно.
 *
 * Ответ — вся карточка целиком, как у правки оценки. `404` — звонка нет или
 * предложения у него нет: например, повторный разбор снял его, пока карточка
 * была открыта.
 */
export const decideDepartmentSuggestion = (callId: string, status: DepartmentSuggestionStatus) =>
  apiRequest<CallDetail>(`/api/calls/${callId}/department-suggestion`, {
    method: 'PUT',
    body: { status },
  });

/**
 * Общая оценка звонка звёздами.
 *
 * Снятие — именно `null`, а не `0`: ноль читался бы как «худшая оценка», а
 * снятая оценка это отсутствие суждения.
 *
 * Ответ — вся карточка целиком, как и у правки правила: выставление звёзд
 * тоже разбор и обновляет `reviewedAt` с `reviewerName`.
 */
export const setCallRating = (callId: string, rating: number | null, comment?: string) =>
  apiRequest<CallDetail>(`/api/calls/${callId}/rating`, {
    method: 'PUT',
    body: { rating, comment },
  });

// ────────────────────────── Чек-листы и правила ─────────────────────────

export const listScorecards = () => apiRequest<Scorecard[]>('/api/scorecards');

/** Каталог доступен любому авторизованному: это описание возможностей, а не данные клиента. */
export const listScorecardTemplates = () =>
  apiRequest<ScorecardTemplate[]>('/api/scorecards/templates');

/**
 * Завести чек-лист из шаблона.
 *
 * **Копируются правила, а не создаётся ссылка.** Дальше чек-лист живёт своей
 * жизнью: наши правки шаблона его не переписывают. Обратное означало бы, что
 * настроенный клиентом чек-лист однажды молча изменится.
 *
 * Один и тот же шаблон можно добавлять сколько угодно раз — у холдинга их
 * будет по маркам и по салонам, для того `name` и нужен.
 */
export const createScorecardFromTemplate = (template: string, name: string) =>
  apiRequest<{
    scorecard: Scorecard;
    /** Сколько правил заведено. */
    rules: number;
    template: string;
  }>('/api/scorecards/from-template', { method: 'POST', body: { template, name } });

export const listRules = (scorecardId: string) =>
  apiRequest<Rule[]>(`/api/scorecards/${scorecardId}/rules`);

export const createRule = (scorecardId: string, body: RuleInput) =>
  apiRequest<Rule>(`/api/scorecards/${scorecardId}/rules`, { method: 'POST', body });

export const updateRule = (scorecardId: string, ruleId: string, body: RuleInput) =>
  apiRequest<Rule>(`/api/scorecards/${scorecardId}/rules/${ruleId}`, { method: 'PUT', body });

export const deleteRule = (scorecardId: string, ruleId: string) =>
  apiRequest<void>(`/api/scorecards/${scorecardId}/rules/${ruleId}`, { method: 'DELETE' });

/** Прогон без сохранения — без него правило настраивают вслепую. */
export const testRule = (body: RuleTestRequest) =>
  apiRequest<RuleTestResponse>('/api/rules/test', { method: 'POST', body });

// ───────────────────── Сотрудники и добавочные ──────────────────────────

export const listEmployees = (filters: EmployeeFilters = {}) =>
  apiRequest<Employee[]>('/api/employees', { query: filters as Record<string, unknown> });

export const createEmployee = (body: EmployeeInput) =>
  apiRequest<Employee>('/api/employees', { method: 'POST', body });

export const updateEmployee = (id: string, body: EmployeeInput) =>
  apiRequest<Employee>(`/api/employees/${id}`, { method: 'PUT', body });

/** Переводит в inactive, а не удаляет: на сотрудника ссылаются звонки. */
export const deactivateEmployee = (id: string) =>
  apiRequest<void>(`/api/employees/${id}`, { method: 'DELETE' });

export const listAssignments = (employeeId: string) =>
  apiRequest<Assignment[]>(`/api/employees/${employeeId}/assignments`);

/**
 * Закрепление подбирает уже принятые звонки: attributedCalls надо показать —
 * иначе действие выглядит как заведение строки, хотя оно переписало историю.
 */
export const createAssignment = (employeeId: string, body: AssignmentInput) =>
  apiRequest<AssignmentCreated>(`/api/employees/${employeeId}/assignments`, {
    method: 'POST',
    body,
  });

/** Закрывает датой, а не удаляет. releasedCalls — сколько звонков отвязалось. */
export const closeAssignment = (employeeId: string, assignmentId: string, activeTo?: string) =>
  apiRequest<AssignmentClosed>(`/api/employees/${employeeId}/assignments/${assignmentId}`, {
    method: 'DELETE',
    query: { activeTo },
  });

// ───────────────────── Пользователи и права доступа ─────────────────────

/**
 * Пользователи тенанта вместе с правами.
 *
 * Создать пользователя нельзя: они заводятся сами при первом входе. Ручка
 * `POST /api/users` требует сервисной учётки Zitadel с правами Management
 * API, которой пока нет.
 */
export const listUsers = () => apiRequest<User[]>('/api/users');

/**
 * Заменяет набор прав **целиком**, а не дополняет.
 *
 * Отправлять надо весь список, включая роли, которые не менялись: иначе
 * отозванная роль осталась бы действующей. Выдать право можно только на
 * узел, который сам администрируешь; проверка идёт до записи, поэтому отказ
 * оставляет прежние права нетронутыми и запрос можно повторить исправленным.
 */
export const replaceUserGrants = (userId: string, grants: GrantInput[]) =>
  apiRequest<{ grants: number }>(`/api/users/${userId}/grants`, { method: 'PUT', body: grants });

/** Возвращает и отозванные: журнал доступа нужен целиком. */
export const listDisclosureGrants = (filters: DisclosureFilters = {}) =>
  apiRequest<DisclosureGrant[]>('/api/disclosure-grants', {
    query: filters as Record<string, unknown>,
  });

/** Только `tenant_admin`: раскрытие между юрлицами — решение уровня компании. */
export const createDisclosureGrant = (body: DisclosureGrantInput) =>
  apiRequest<DisclosureGrant>('/api/disclosure-grants', { method: 'POST', body });

/**
 * Отзывает, а не удаляет: журнал за прошлые периоды должен остаться
 * объяснимым. Доступ закрывается сразу — звонки узла исчезают из выдачи
 * следующим же запросом.
 */
export const revokeDisclosureGrant = (id: string) =>
  apiRequest<{ id: string; revokedAt: string }>(`/api/disclosure-grants/${id}`, {
    method: 'DELETE',
  });

/**
 * Удаление маршрута — настоящее: привязка это маршрут, а не история.
 * Принятые по ней звонки остаются, но новые с этого домена уйдут в карантин,
 * а не в узел по умолчанию.
 */
export const deleteBinding = (sourceId: string, bindingId: string) =>
  apiRequest<void>(`/api/sources/${sourceId}/bindings/${bindingId}`, { method: 'DELETE' });

// ─────────────────────────── Платформенные АТС ──────────────────────────

/**
 * Справочник станций платформы.
 *
 * Закрыт флагом `isPlatformStaff`, и админ клиента получает **404, а не
 * 403**: существование платформенного справочника тоже информация. Значит
 * 404 из этих ручек — не «сломалось», а «раздел не для вас», и показывать
 * его надо соответственно.
 */
export const listServers = () => apiRequest<PbxServer[]>('/api/servers');

/** Ключ приёма возвращается ровно один раз: дальше в базе только хэш. */
export const createServer = (body: PbxServerInput) =>
  apiRequest<PbxServerWithSecret>('/api/servers', { method: 'POST', body });

/**
 * Прежний ключ перестаёт работать немедленно, и станция замолчит, пока новый
 * не пропишут в `json_cdr.conf.xml`. Так и задумано: ротация обычно означает,
 * что прежний ключ утёк.
 */
/**
 * Разрешить разделение говорящих по каналам записи.
 *
 * Отдельная ручка, а не поле при заведении: решение принимают после того,
 * как посмотрели записи. Стерео бывает двух видов, и в заголовке файла они
 * неотличимы — плечи по каналам и дубль одного микса в оба канала. Во
 * втором случае делить нечего, и «разделение» окажется выдумкой.
 */
export const setServerRecordingStereo = (id: string, recordingStereo: boolean) =>
  apiRequest<PbxServer>(`/api/servers/${id}/recording-stereo`, {
    method: 'PUT',
    body: { recordingStereo },
  });

export const rotateServerSecret = (id: string) =>
  apiRequest<PbxServerWithSecret>(`/api/servers/${id}/rotate-secret`, { method: 'POST' });

export const createScorecard = (name: string) =>
  apiRequest<Scorecard>('/api/scorecards', { method: 'POST', body: { name } });

export const renameScorecard = (id: string, name: string) =>
  apiRequest<Scorecard>(`/api/scorecards/${id}`, { method: 'PUT', body: { name } });

/**
 * Удаление настоящее, а не архивация.
 *
 * Прошлые оценки от него не портятся: в `call_score_results` лежат снимки
 * имени, веса и критичности каждого правила на момент оценки — карточка
 * старого звонка останется объяснимой.
 */
export const deleteScorecard = (id: string) =>
  apiRequest<void>(`/api/scorecards/${id}`, { method: 'DELETE' });

// ─────────────────────── Справочники со станции ─────────────────────────

/**
 * Добавочные, присланные станцией.
 *
 * Область режется в БД: список добавочных с именами — это, как правило,
 * фамилии людей, то есть персональные данные юрлица. Администратор соседнего
 * салона получает **пустой массив, а не 403**: существование чужих записей
 * тоже информация.
 */
export const listExtensions = (sourceId: string, filters: DirectoryFilters = {}) =>
  apiRequest<DirectoryExtension[]>(`/api/sources/${sourceId}/extensions`, {
    query: filters as Record<string, unknown>,
  });

export const listDestinations = (sourceId: string, filters: DirectoryFilters = {}) =>
  apiRequest<DirectoryDestination[]>(`/api/sources/${sourceId}/destinations`, {
    query: filters as Record<string, unknown>,
  });

export const listQueues = (sourceId: string, filters: DirectoryFilters = {}) =>
  apiRequest<DirectoryQueue[]>(`/api/sources/${sourceId}/queues`, {
    query: filters as Record<string, unknown>,
  });

export const listGateways = (sourceId: string, filters: DirectoryFilters = {}) =>
  apiRequest<DirectoryGateway[]>(`/api/sources/${sourceId}/gateways`, {
    query: filters as Record<string, unknown>,
  });

/**
 * Отнести запись справочника к подразделению.
 *
 * Единственный способ задать узел: синхронизатор их не трогает — куда отнести
 * добавочный, решает администратор, и станция это переписывать не должна.
 *
 * Права проверяются **на обоих концах переноса**: иначе администратор одного
 * салона перетащил бы к себе чужой добавочный вместе с доступом к разговорам.
 */
export const assignExtensionUnit = (sourceId: string, extensionId: string, unitId: string) =>
  /**
   * Отвечает `movedCalls` — сколько прошлых звонков переехало следом.
   * Раскладку делают уже после того, как звонки пошли, и без переноса они
   * остались бы на узле маршрута. Число надо показать: иначе действие
   * выглядит как настройка на будущее, хотя оно переписало историю.
   */
  apiRequest<{ movedCalls: number }>(`/api/sources/${sourceId}/extensions/${extensionId}`, {
    method: 'PUT',
    body: { unitId },
  });

export const assignQueueUnit = (sourceId: string, queueId: string, unitId: string) =>
  apiRequest<void>(`/api/sources/${sourceId}/queues/${queueId}`, {
    method: 'PUT',
    body: { unitId },
  });

export const updateDestinationRouting = (
  sourceId: string,
  destinationId: string,
  body: DestinationRoutingInput
) =>
  apiRequest<void>(`/api/sources/${sourceId}/destinations/${destinationId}`, {
    method: 'PUT',
    body,
  });

/* ------------------------------------------------------------------ */
/* Дашборд и отчёты                                                     */
/* ------------------------------------------------------------------ */

/**
 * Плитки первого экрана.
 *
 * `compare` добавляет `previous` — тот же набор за предшествующий отрезок
 * **той же длины**, а не за «прошлый месяц»: на десятое число сравнение
 * десяти дней с тридцатью показало бы обвал любого показателя.
 */
export const getDashboardMetrics = (filters: ReportFilters, compare = false) =>
  apiRequest<DashboardMetrics>('/api/dashboard/metrics', {
    query: { ...filters, compare: compare ? true : undefined },
  });

export const getSummary = (filters: ReportFilters, groupBy: 'unit' | 'source' | 'none' = 'unit') =>
  apiRequest<SummaryReportData>('/api/analytics/summary', { query: { ...filters, groupBy } });

/**
 * Перехват между отделами.
 *
 * Строка — отдел, **которому звонили**, а не тот, к которому отнесён звонок:
 * оси разные, и складывать их в одну таблицу нельзя.
 */
export const getHandoff = (filters: ReportFilters = {}) =>
  apiRequest<HandoffReport>('/api/analytics/handoff', {
    query: filters as Record<string, unknown>,
  });

export const getActivityHeatmap = (filters: ReportFilters) =>
  apiRequest<HeatmapData>('/api/analytics/activity-heatmap', { query: { ...filters } });

export const getScoreDynamics = (
  filters: ReportFilters,
  bucket: 'day' | 'week' = 'day',
  groupBy: 'unit' | 'none' = 'unit'
) =>
  apiRequest<ScoreDynamicsData>('/api/analytics/score-dynamics', {
    query: { ...filters, bucket, groupBy },
  });

export const getTopViolations = (filters: ReportFilters, limit = 10) =>
  apiRequest<TopViolationsData>('/api/analytics/top-violations', { query: { ...filters, limit } });

export const getViolations = (filters: ReportFilters, groupBy: 'unit' | 'employee' = 'unit') =>
  apiRequest<ViolationsData>('/api/analytics/violations', { query: { ...filters, groupBy } });

export const getRating = (
  filters: ReportFilters,
  /* `lost` появился вместе с разделением «не взял» и «потеряно»: сортировка
     по потерям ставит наверх тех, у кого звонки правда пропали, а не тех, кто
     просто не успел первым в ринг-группе. */
  sortBy: 'score' | 'calls' | 'missed' | 'lost' | 'talk' = 'score'
) => apiRequest<RatingData>('/api/analytics/rating', { query: { ...filters, sortBy } });

/**
 * Разрез сотрудника по правилам — то, что руководитель откроет перед
 * разговором с человеком. Сотрудник вне видимых узлов даёт пустой разрез, а
 * не чужие данные.
 */
export const getEmployeeRules = (employeeId: string, filters: ReportFilters) =>
  apiRequest<EmployeeRulesData>(`/api/analytics/rating/${employeeId}/rules`, {
    query: { from: filters.from, to: filters.to },
  });

/**
 * Выгрузка в CSV.
 *
 * Формат считает сервер под Excel в русской и казахстанской локали — точка с
 * запятой, BOM, запятая в дробях. Клиент ничего не переписывает: любая правка
 * здесь ломает ровно то, ради чего формат такой.
 *
 * Для `report=calls` принимаются все фильтры `/api/calls`, поэтому параметры
 * передаются как есть, а не сводятся к `ReportFilters`.
 */
export const exportReport = (report: ReportKind, params: Record<string, unknown>) =>
  apiDownload('/api/reports/export', { ...params, report });

/* ------------------------------------------------------------------ */
/* Глобальный администратор                                            */
/* ------------------------------------------------------------------ */

/**
 * Вся ветка `/api/admin` отвечает **404, а не 403** тому, кто не сотрудник
 * платформы: существование этих экранов — тоже сведения.
 *
 * Отсюда два следствия для интерфейса: пункт меню прячется по
 * `isPlatformStaff` из `/api/me`, а не по ответу сервера; и обработчика «нет
 * прав» здесь нет — его не будет, будет «не найдено».
 */

export const listPendingOrganizations = (limit = 50) =>
  apiRequest<{ items: PendingOrganization[] }>('/api/admin/pending-organizations', {
    query: { limit },
  });

/**
 * Убрать организацию из списка, ничего не подключая — для чужих: в Zitadel
 * есть организации, которым Callog не продавали, и их попытки должны уходить
 * с экрана, а не копиться вечным напоминанием.
 */
export const hidePendingOrganization = (orgId: string) =>
  apiRequest<void>(`/api/admin/pending-organizations/${encodeURIComponent(orgId)}`, {
    method: 'DELETE',
  });

export const listAdminTenants = () => apiRequest<{ items: AdminTenant[] }>('/api/admin/tenants');

/**
 * Завести клиента. Сервер делает это одной транзакцией: тенант, корень
 * дерева и привязка организации — частичного результата не бывает.
 */
export const createAdminTenant = (body: AdminTenantInput) =>
  apiRequest<AdminTenant>('/api/admin/tenants', { method: 'POST', body });

export const getAdminTenant = (id: string) =>
  apiRequest<AdminTenant & { organizations?: AdminOrganization[] }>(`/api/admin/tenants/${id}`);

export const listTenantAdmins = (id: string) =>
  apiRequest<{ items: AdminOfTenant[] }>(`/api/admin/tenants/${id}/admins`);

/**
 * Назначить администратора клиента.
 *
 * Спрашивать «приглашение это или выдача прав» не надо: пользователь не
 * может этого знать, и решает сервер. Тот, кто уже входил, получает права
 * сразу (`source: user`); тот, кто не входил, — приглашение (`invite`),
 * которое применится при первом входе. Оба случая — `201`.
 *
 * Повторный вызов с той же почтой не ошибка и второго приглашения не заводит.
 */
export const addTenantAdmin = (id: string, email: string) =>
  apiRequest<AdminOfTenant>(`/api/admin/tenants/${id}/admins`, {
    method: 'POST',
    body: { email },
  });

/** `adminId` уходит как есть: uuid у вошедшего, число у приглашения. */
export const removeTenantAdmin = (id: string, adminId: string) =>
  apiRequest<void>(`/api/admin/tenants/${id}/admins/${encodeURIComponent(adminId)}`, {
    method: 'DELETE',
  });

/* ------------------------------------------------------------------ */
/* Готовые наборы тегов                                                 */
/* ------------------------------------------------------------------ */

export const listTagTemplates = () => apiRequest<TagTemplate[]>('/api/tags/templates');

/**
 * Применить набор.
 *
 * `rules: false` заводит только словарь — нужный режим, пока не накоплен
 * транскрипт: правила по фразам не сработают, а метки расставлять руками уже
 * можно. Поэтому это выбор при применении, а не спрятанный флаг.
 */
export const applyTagTemplate = (template: string, rules: boolean) =>
  apiRequest<TagTemplateResult>('/api/tags/from-template', {
    method: 'POST',
    body: { template, rules },
  });

/* ------------------------------------------------------------------ */
/* Хранилища записей и сроки хранения                                   */
/* ------------------------------------------------------------------ */

/** Всё под `platform_staff`; остальным — 404, а не 403. */
export const listStorageProfiles = () => apiRequest<StorageProfile[]>('/api/storage-profiles');

export const createStorageProfile = (body: StorageProfileInput) =>
  apiRequest<StorageProfile>('/api/storage-profiles', { method: 'POST', body });

/** Пустые `accessKeyId` и `secretAccessKey` означают «оставить как было». */
export const updateStorageProfile = (id: string, body: StorageProfileInput) =>
  apiRequest<StorageProfile>(`/api/storage-profiles/${id}`, { method: 'PUT', body });

/** `409`, пока на профиль ссылается станция или источник: см. `servers` и `sources`. */
export const deleteStorageProfile = (id: string) =>
  apiRequest<void>(`/api/storage-profiles/${id}`, { method: 'DELETE' });

/** Отвечает 200 и на неудачу: `ok: false` — результат проверки, а не сбой запроса. */
export const testStorageProfile = (id: string) =>
  apiRequest<StorageTestResult>(`/api/storage-profiles/${id}/test-connection`, { method: 'POST' });

/** `null` — запасное хранилище платформы. Это не «никуда»: оно задано окружением API. */
export const setServerStorage = (serverId: string, storageProfileId: string | null) =>
  apiRequest<void>(`/api/servers/${serverId}/storage`, {
    method: 'PUT',
    body: { storageProfileId },
  });

/** Хранилище источника **перебивает** хранилище станции. */
export const setSourceStorage = (sourceId: string, storageProfileId: string | null) =>
  apiRequest<void>(`/api/sources/${sourceId}/storage`, {
    method: 'PUT',
    body: { storageProfileId },
  });

/**
 * Срок хранения записей клиента. `null` — не удалять.
 *
 * Уменьшение срока удаляет записи безвозвратно ближайшим фоновым проходом,
 * без корзины: форма обязана предупредить об этом словами.
 */
export const setTenantRetention = (tenantId: string, retentionDays: number | null) =>
  apiRequest<void>(`/api/admin/tenants/${tenantId}/retention`, {
    method: 'PUT',
    body: { retentionDays },
  });

/* ------------------------------------------------------------------ */
/* Мои оповещения                                                       */
/* ------------------------------------------------------------------ */

/**
 * Всегда «мои»: человек берётся из токена, чужие настройки этой веткой не
 * читаются и не правятся.
 */
export const getNotificationSettings = () =>
  apiRequest<NotificationSettings>('/api/notification-settings');

/** Обе правки возвращают настройки целиком — перечитывать после них не нужно. */
export const setNotificationContacts = (body: NotificationContactsInput) =>
  apiRequest<NotificationSettings>('/api/notification-settings/contacts', {
    method: 'PUT',
    body,
  });

/** Оба `false` — отписка; отдельной кнопки «отписаться» не нужно. */
export const setNotificationSubscription = (
  ruleId: string,
  body: { inApp: boolean; webhook: boolean }
) =>
  apiRequest<NotificationSettings>(`/api/notification-settings/rules/${ruleId}`, {
    method: 'PUT',
    body,
  });

/* ------------------------------------------------------------------ */
/* Настройка разбора                                                    */
/* ------------------------------------------------------------------ */

/** Читает любой авторизованный: по промту видно, что модель искала в разговоре. */
export const getAnalysisSettings = () => apiRequest<AnalysisSettings>('/api/analysis-settings');

/** Только администратор клиента. `null` в промте — «вернуть стандартный». */
export const updateAnalysisSettings = (body: AnalysisSettingsInput) =>
  apiRequest<AnalysisSettings>('/api/analysis-settings', { method: 'PUT', body });

export const listVocabulary = () => apiRequest<VocabularyItem[]>('/api/vocabulary');

/** `409` на дубль; регистр различием не считается. */
export const createVocabularyItem = (body: VocabularyInput) =>
  apiRequest<VocabularyItem>('/api/vocabulary', { method: 'POST', body });

export const updateVocabularyItem = (id: string, body: VocabularyInput) =>
  apiRequest<VocabularyItem>(`/api/vocabulary/${id}`, { method: 'PUT', body });

export const deleteVocabularyItem = (id: string) =>
  apiRequest<void>(`/api/vocabulary/${id}`, { method: 'DELETE' });

/* ------------------------------------------------------------------ */
/* Исходящие интеграции                                                 */
/* ------------------------------------------------------------------ */

/**
 * Только администратор: интеграция отправляет содержание разговоров наружу, и
 * цена ошибки здесь выше, чем у любой другой настройки.
 *
 * В списке приезжает сводка по доставкам (`pending`, `failed`,
 * `lastSuccessAt`, `lastErrorAt`): растущий `pending` при свежем `lastErrorAt`
 * — это лежащая CRM, и заметить это лучше здесь, чем в журнале.
 */
export const listIntegrations = () => apiRequest<Integration[]>('/api/integrations');

export const getIntegration = (id: string) => apiRequest<Integration>(`/api/integrations/${id}`);

/**
 * Какие CRM умеет **эта сборка** и что они просят настроить.
 *
 * Форма строится по этому ответу, а не по зашитому во фронтенде списку полей:
 * иначе новая CRM означала бы правку и бэкенда, и фронтенда.
 */
export const listIntegrationKinds = () => apiRequest<IntegrationKind[]>('/api/integrations/kinds');

/** `authSecret` принимается только здесь; дальше — через `/secrets`. */
export const createIntegration = (body: IntegrationInput) =>
  apiRequest<Integration>('/api/integrations', { method: 'POST', body });

/**
 * Секреты этой ручкой не меняются — и отправлять их тут нельзя: форма их не
 * показывает, а «слать форму целиком» затёрло бы токен пустым значением.
 */
export const updateIntegration = (id: string, body: IntegrationInput) =>
  apiRequest<Integration>(`/api/integrations/${id}`, { method: 'PUT', body });

/** Вместе с интеграцией уходят подписки, строки доставки и соответствия. */
export const deleteIntegration = (id: string) =>
  apiRequest<void>(`/api/integrations/${id}`, { method: 'DELETE' });

/**
 * Заменить секреты.
 *
 * Отсутствующее поле оставляет прежнее значение, пустая строка — стирает.
 * Поэтому поле, которого человек не трогал, в тело не попадает вовсе.
 */
export const setIntegrationSecrets = (id: string, body: IntegrationSecretsInput) =>
  apiRequest<Integration>(`/api/integrations/${id}/secrets`, { method: 'PUT', body });

/**
 * Проверка связи настоящими адресом и учётными данными.
 *
 * **Всегда 200**, результат в теле: `ok: false` с текстом — это ответ, а не
 * сбой, и обрабатывать его как ошибку запроса нельзя.
 */
export const testIntegration = (id: string) =>
  apiRequest<IntegrationTestResult>(`/api/integrations/${id}/test`, { method: 'POST' });

/** Чему соответствуют наши источники и цели в чужой системе. */
export const getIntegrationMappings = async (id: string) => {
  const page = await apiRequest<{ mappings?: IntegrationMapping[] }>(
    `/api/integrations/${id}/mappings`
  );
  return page.mappings ?? [];
};

/**
 * Правка соответствий — **только изменённых строк**, а не набора целиком.
 *
 * Источников у инстанса бывает полтораста, и полная замена означала бы, что
 * две открытые формы затирают друг друга. Пустой `externalId` убирает строку.
 */
export const putIntegrationMappings = (id: string, body: IntegrationMappingsInput) =>
  apiRequest<{ mappings?: IntegrationMapping[] }>(`/api/integrations/${id}/mappings`, {
    method: 'PUT',
    body,
  });

/**
 * Справочники чужой системы: салоны, источники обращения, пользователи.
 *
 * Спрашиваются у уже созданной интеграции — для запроса нужны её адрес и
 * секрет. Всегда `200`: недоступная CRM это её состояние, а не наша ошибка.
 */
export const getIntegrationDictionaries = (id: string) =>
  apiRequest<IntegrationDictionaries>(`/api/integrations/${id}/dictionaries`);

/**
 * Проверка справочника контактов — **отдельно от проверки связи**: адреса и
 * ключи у них разные, и общая кнопка соврала бы в обе стороны.
 *
 * Номер вводит человек: проверка имеет смысл только на том, про который он
 * знает ответ. Нормализует сервер.
 *
 * Всегда `200`, кроме негодного номера — там `400`. Значит, отказ читается из
 * тела, а не из кода: `ok: false` с текстом — это ответ.
 */
export const testIntegrationLookup = (id: string, phone: string) =>
  apiRequest<IntegrationLookupTest>(`/api/integrations/${id}/test-lookup`, {
    method: 'POST',
    body: { phone },
  });

/** Новые сверху. `limit` не больше 200. */
export const listOutboundDeliveries = (filters: DeliveryFilters = {}) =>
  apiRequest<OutboundDelivery[]>('/api/integrations/deliveries', {
    query: filters as Record<string, unknown>,
  });

/**
 * Сверка оценки с конверсией.
 *
 * Группировка по полосе, а не по баллу: «звонки на 73 против звонков на 74» —
 * шум. `rate` считает сервер и считает от **закрытых** сделок: своя формула
 * разойдётся с нашей ровно на незакрытых.
 */
export const getConversion = (filters: ConversionFilters = {}) =>
  apiRequest<ConversionRow[]>('/api/integrations/conversion', {
    query: filters as Record<string, unknown>,
  });

/* ------------------------------------------------------------------ */
/* Коллтрекинг                                                          */
/* ------------------------------------------------------------------ */

/** Читает любой авторизованный: состав пула объясняет карточку звонка. */
export const listPools = () => apiRequest<CtPool[]>('/api/calltracking/pools');

export const createPool = (body: CtPoolInput) =>
  apiRequest<CtPool>('/api/calltracking/pools', { method: 'POST', body });

/**
 * `409` при переводе в `single_site`, когда лендингов у пула уже несколько.
 * Текст отказа написан для человека — показывать как есть.
 */
export const updatePool = (id: string, body: CtPoolInput) =>
  apiRequest<CtPool>(`/api/calltracking/pools/${id}`, { method: 'PATCH', body });

export const listCtNumbers = (poolId?: string) =>
  apiRequest<CtNumber[]>('/api/calltracking/numbers', { query: { pool: poolId } });

/** `dynamic` без `poolId` — 400: такой номер некому выдавать. */
export const updateCtNumber = (id: string, body: CtNumberPatch) =>
  apiRequest<CtNumber>(`/api/calltracking/numbers/${id}`, { method: 'PATCH', body });

/**
 * Принять появившийся на станции маршрут.
 *
 * Номер перестаёт быть ручным, связывается с назначением, узел снимается —
 * дальше он берётся из справочника. Нажимают это, когда маршрут завели сами:
 * перечислили серию на станции.
 *
 * `400`, если назначения с таким номером на станции уже нет: синхронизация не
 * прошла либо маршрут убрали. Текст показывать как есть.
 *
 * **Сервер не делает этого сам намеренно**: отличить «мы перечислили серию» от
 * «номер увели на отдел» по данным нельзя — и то и другое выглядит как
 * появившееся назначение, а тихо выкинуть номер из работающего пула хуже, чем
 * спросить.
 */
export const acceptStationRoute = (id: string) =>
  apiRequest<void>(`/api/calltracking/numbers/${id}/accept-station-route`, { method: 'POST' });

/** Что заведено на станции и ещё не в коллтрекинге. */
export const listAvailableNumbers = () =>
  apiRequest<CtAvailableNumber[]>('/api/calltracking/available-numbers');

/**
 * Зачисление номеров.
 *
 * `dryRun: true` ничего не пишет и возвращает состав — и это не формальность:
 * серия задаётся хвостом номера, и городской попадает в серию по хвосту вместе
 * с рекламными. Подтверждение шлёт **тот же** запрос с `dryRun: false`.
 */
export const enrollNumbers = (body: CtEnrollRequest) =>
  apiRequest<CtEnrollResult>('/api/calltracking/numbers', { method: 'POST', body });

export const listCtSites = () => apiRequest<CtSite[]>('/api/calltracking/sites');

export const createCtSite = (body: CtSiteInput) =>
  apiRequest<CtSite>('/api/calltracking/sites', { method: 'POST', body });

export const updateCtSite = (id: string, body: CtSiteInput) =>
  apiRequest<CtSite>(`/api/calltracking/sites/${id}`, { method: 'PATCH', body });

export const listStaticRules = (siteId?: string) =>
  apiRequest<CtStaticRule[]>('/api/calltracking/static-rules', { query: { site: siteId } });

export const createStaticRule = (body: CtStaticRuleInput) =>
  apiRequest<CtStaticRule>('/api/calltracking/static-rules', { method: 'POST', body });

/** Переименование места размещения — правкой: на `id` ссылаются отчёты. */
export const updateStaticRule = (id: string, body: CtStaticRuleInput) =>
  apiRequest<CtStaticRule>(`/api/calltracking/static-rules/${id}`, { method: 'PATCH', body });

export const deleteStaticRule = (id: string) =>
  apiRequest<void>(`/api/calltracking/static-rules/${id}`, { method: 'DELETE' });

/**
 * Какая реклама приводит звонки.
 *
 * **Без отчёта по лендингам опасен**: покажет красивую картинку по тем
 * переходам, что доехали, и умолчит о потерянных до звонка.
 */
export const getCtSourcesReport = (filters: CtSourcesFilters = {}) =>
  apiRequest<CtSourcesReport>('/api/analytics/calltracking/sources', {
    query: filters as Record<string, unknown>,
  });

/**
 * Доезжает ли коллтрекинг до посетителя.
 *
 * Фильтров, кроме периода, нет: `unit` здесь не принимается — пул принадлежит
 * подразделению целиком, и резать его тем же подразделением нечего.
 */
export const getCtSitesReport = (period: { from?: string; to?: string } = {}) =>
  apiRequest<CtSitesReport>('/api/analytics/calltracking/sites', {
    query: period as Record<string, unknown>,
  });

/** Служебное: кому сейчас выдан номер и по какому переходу. */
export const listCtSessions = (filters: CtSessionFilters = {}) =>
  apiRequest<CtSession[]>('/api/calltracking/sessions', {
    query: filters as Record<string, unknown>,
  });

/* ------------------------------------------------------------------ */
/* Цели звонка                                                          */
/* ------------------------------------------------------------------ */

/**
 * Что считать успехом — по-разному у разной рекламы.
 *
 * Целевым раньше считался звонок, где клиент говорил дольше порога. Это время,
 * а не содержание: «сорок секунд возмущался, что не туда попал» проходило, а
 * «да, записывайте на четверг» за восемь секунд — нет.
 */
export const listCallGoals = async (status?: CallGoalStatus) => {
  const page = await apiRequest<{ goals?: CallGoal[] }>('/api/call-goals', { query: { status } });
  return page.goals ?? [];
};

export const createCallGoal = (body: CallGoalInput) =>
  apiRequest<CallGoal>('/api/call-goals', { method: 'POST', body });

export const updateCallGoal = (id: string, body: CallGoalInput) =>
  apiRequest<CallGoal>(`/api/call-goals/${id}`, { method: 'PUT', body });

export const deleteCallGoal = (id: string) =>
  apiRequest<void>(`/api/call-goals/${id}`, { method: 'DELETE' });

/**
 * Примерка на истории. Ничего не пишет.
 *
 * Пересчёта прошлых вердиктов ручкой **нет и не будет**: он меняет цифры в
 * отчётах за закрытые периоды, по которым уже принимали решения, а проход по
 * году архива идёт минуты — HTTP дал бы таймаут прокси и иллюзию, что ничего
 * не произошло. Делается из консоли администратором.
 */
export const dryRunCallGoal = (body: CallGoalDryRunInput) =>
  apiRequest<CallGoalDryRun>('/api/call-goals/dry-run', { method: 'POST', body });

/* ------------------------------------------------------------------ */
/* Качество обработки по источникам                                     */
/* ------------------------------------------------------------------ */

/** Сколько звонков мы потеряли сами — то, о чём отчёт по источникам молчит. */
export const getCtHandlingReport = (filters: CtSourcesFilters = {}) =>
  apiRequest<CtHandlingReport>('/api/analytics/calltracking/handling', {
    query: filters as Record<string, unknown>,
  });

/* ------------------------------------------------------------------ */
/* Названия кампаний                                                    */
/* ------------------------------------------------------------------ */

/** Google Ads названий кампаний не отдаёт: в ValueTrack одни идентификаторы. */
export const listAdNames = async (source?: string) => {
  const page = await apiRequest<{ names?: AdName[] }>('/api/analytics/calltracking/ad-names', {
    query: { source },
  });
  return page.names ?? [];
};

/**
 * Пачкой, до 1000 строк. **Проверка идёт по всей пачке до записи**: ошибка в
 * середине оставила бы половину названий загруженными, а вызывающий повторил
 * бы загрузку с тем же результатом. Существующее перетирается — кампанию
 * переименовали, верно новое имя.
 */
export const putAdNames = (names: AdNameInput[]) =>
  apiRequest<{ saved?: number }>('/api/analytics/calltracking/ad-names', {
    method: 'PUT',
    body: { names },
  });

export const deleteAdName = (source: string, kind: string, externalId: string) =>
  apiRequest<void>('/api/analytics/calltracking/ad-names', {
    method: 'DELETE',
    query: { source, kind, externalId },
  });

/* ------------------------------------------------------------------ */
/* Дополнения промтов по направлениям                                   */
/* ------------------------------------------------------------------ */

/** Отдаёт только те направления, у которых дополнения заведены. */
export const listUnitAddenda = async () => {
  const page = await apiRequest<{ units?: UnitPromptAddendum[] }>('/api/analysis-settings/units');
  return page.units ?? [];
};

/** Пустой ответ — законный: своих дополнений у узла нет. Не 404 и не ошибка. */
export const getUnitAddendum = (unitId: string) =>
  apiRequest<UnitPromptAddendum>(`/api/analysis-settings/units/${unitId}`);

export const setUnitAddendum = (unitId: string, body: UnitPromptAddendumInput) =>
  apiRequest<UnitPromptAddendum>(`/api/analysis-settings/units/${unitId}`, {
    method: 'PUT',
    body,
  });

export const deleteUnitAddendum = (unitId: string) =>
  apiRequest<void>(`/api/analysis-settings/units/${unitId}`, { method: 'DELETE' });

/**
 * Чем кормили звонок.
 *
 * Пустой список — **законный ответ**: звонок мог быть принят до появления этой
 * записи либо не ходить на обработку вовсе — пропущенный, запись выключена на
 * привязке, станция не сделала файл.
 */
export const getAnalysisInput = async (callId: string) => {
  const page = await apiRequest<{ stages?: AnalysisInputEntry[] }>(
    `/api/calls/${callId}/analysis-input`
  );
  return page.stages ?? [];
};
